/**
 * Shared push helpers used by BOTH the app window and the service worker.
 *
 * Kept dependency-free (no axios, no DOM-only APIs) so it can be bundled into
 * `sw.ts` without dragging the API client into the worker.
 *
 * The worker needs the VAPID key and API base URL to re-subscribe on its own
 * when the browser rotates the push subscription (the `pushsubscriptionchange`
 * event) — a moment when no app window may be open, so the worker can't read
 * `import.meta.env` or the app stores. The window stashes them in Cache Storage
 * (reachable from both contexts, survives worker restarts) whenever it
 * registers a subscription; the worker reads them back when it needs to
 * recover.
 */
export const PUSH_CONFIG_CACHE = 'hoopla-push-config:v1';
// Fake absolute URL used purely as a Cache Storage key — never fetched.
const PUSH_CONFIG_KEY = 'https://push-config.hoopla.local/config.json';
export const PUSH_SUBSCRIBE_PATH = '/api/v1/push/subscribe';

export interface PushConfig {
  vapidPublicKey: string;
  baseUrl: string;
}

export async function savePushConfig(config: PushConfig): Promise<void> {
  if (typeof caches === 'undefined') return;
  try {
    const cache = await caches.open(PUSH_CONFIG_CACHE);
    await cache.put(
      PUSH_CONFIG_KEY,
      new Response(JSON.stringify(config), {
        headers: { 'Content-Type': 'application/json' },
      }),
    );
  } catch (err) {
    // Best-effort: this cache only feeds the worker's self-recovery. A write
    // failure (private mode, quota, restricted storage) must never abort the
    // actual push subscription, which is the caller's real goal.
    console.warn('[push] failed to cache push config', err);
  }
}

export async function clearPushConfig(): Promise<void> {
  if (typeof caches === 'undefined') return;
  try {
    await caches.delete(PUSH_CONFIG_CACHE);
  } catch (err) {
    // Best-effort, mirroring savePushConfig: failing to drop the recovery
    // config must never block opt-out.
    console.warn('[push] failed to clear push config', err);
  }
}

export async function loadPushConfig(): Promise<PushConfig | null> {
  if (typeof caches === 'undefined') return null;
  try {
    const cache = await caches.open(PUSH_CONFIG_CACHE);
    const res = await cache.match(PUSH_CONFIG_KEY);
    if (!res) return null;
    const cfg = (await res.json()) as Partial<PushConfig>;
    if (!cfg.vapidPublicKey || !cfg.baseUrl) return null;
    return { vapidPublicKey: cfg.vapidPublicKey, baseUrl: cfg.baseUrl };
  } catch {
    return null;
  }
}

/** Join a base URL and an absolute API path without doubling the slash. */
export function joinUrl(baseUrl: string, path: string): string {
  return baseUrl.replace(/\/+$/, '') + path;
}

export function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const buffer = new ArrayBuffer(rawData.length);
  const out = new Uint8Array(buffer);
  for (let i = 0; i < rawData.length; i++) out[i] = rawData.charCodeAt(i);
  return out;
}

/**
 * Whether an existing subscription is safe to reuse for the given VAPID key.
 * After a key rotation (or a staging/prod mismatch) the old subscription is
 * bound to the dead key and silently receives nothing, so callers must detect
 * that and re-subscribe.
 *
 * Returns `true` (reuse) unless we can *prove* the keys differ. Some engines
 * (older iOS/Safari, and Firefox in places) don't echo `applicationServerKey`
 * back even for a perfectly valid subscription; treating that absence as a
 * mismatch would unsubscribe + re-mint the endpoint on every login (and
 * `usePushSubscription` runs on every authenticated mount), churning the
 * subscription on exactly the platforms most likely to return null. So an
 * unreadable key means "can't prove a mismatch" → keep what we have.
 */
export function subscriptionMatchesKey(sub: PushSubscription, vapidPublicKey: string): boolean {
  const existing = sub.options.applicationServerKey;
  if (!existing) return true;
  const want = urlBase64ToUint8Array(vapidPublicKey);
  const have = new Uint8Array(existing);
  if (have.length !== want.length) return false;
  for (let i = 0; i < have.length; i++) {
    if (have[i] !== want[i]) return false;
  }
  return true;
}
